import {
	createQrImageBuilder,
	getQrDataToEncode,
	TQrDesign,
} from '@/generator/client'
import { TQrContent } from '@/generator/shared'

const QR_SVG_CACHE_MAX_SIZE = 300

class LRUCache {
	readonly storageKey = 'qr-svg-cache-v2'
	private readonly legacyStorageKey = 'qr-svg-cache'
	maxSize: number
	cache: Map<string, Promise<string>>
	private resolvedCache: Map<string, string>

	constructor(maxSize: number) {
		this.maxSize = maxSize
		this.cache = new Map()
		this.resolvedCache = new Map()

		this.loadLocalStorageCache()
	}

	private loadLocalStorageCache() {
		if (typeof window === 'undefined') return

		try {
			localStorage.removeItem(this.legacyStorageKey)

			const stored = localStorage.getItem(this.storageKey)
			if (!stored) return

			const parsed = JSON.parse(stored) as
				| Record<string, string>
				| [string, string][]
			const entries = Array.isArray(parsed) ? parsed : Object.entries(parsed)
			const limitedEntries = entries.slice(-this.maxSize)
			this.cache.clear()
			this.resolvedCache.clear()
			for (const [key, value] of limitedEntries) {
				this.resolvedCache.set(key, value)
				this.cache.set(key, Promise.resolve(value))
			}
		} catch {
			// Ignore errors
		}
	}

	private persistResolvedCache() {
		if (typeof window === 'undefined') return

		let entries = Array.from(this.resolvedCache.entries()).slice(-this.maxSize)

		while (entries.length > 0) {
			try {
				localStorage.setItem(this.storageKey, JSON.stringify(entries))
				return
			} catch {
				entries = entries.slice(1)
			}
		}

		try {
			localStorage.removeItem(this.storageKey)
		} catch {
			// Ignore errors
		}
	}

	get(key: string): Promise<string> | undefined {
		if (!this.cache.has(key)) return undefined

		const value = this.cache.get(key)!
		this.cache.delete(key)
		this.cache.set(key, value)

		const resolvedValue = this.resolvedCache.get(key)
		if (resolvedValue !== undefined) {
			this.resolvedCache.delete(key)
			this.resolvedCache.set(key, resolvedValue)
			this.persistResolvedCache()
		}

		return value
	}

	set(key: string, value: Promise<string>) {
		if (this.cache.has(key)) {
			this.cache.delete(key)
			this.resolvedCache.delete(key)
		} else if (this.cache.size === this.maxSize) {
			const firstKey = this.cache.keys().next().value
			if (firstKey) {
				this.cache.delete(firstKey)
				this.resolvedCache.delete(firstKey)
			}
		}

		this.cache.set(key, value)

		void value
			.then((resolvedValue) => {
				if (this.cache.get(key) !== value) return

				this.resolvedCache.delete(key)
				this.resolvedCache.set(key, resolvedValue)
				this.persistResolvedCache()
			})
			.catch(() => {
				if (this.cache.get(key) !== value) return

				this.cache.delete(key)
				this.resolvedCache.delete(key)
				this.persistResolvedCache()
			})
	}

	delete(key: string) {
		this.cache.delete(key)
		this.resolvedCache.delete(key)
		this.persistResolvedCache()
	}

	clear() {
		this.cache.clear()
		this.resolvedCache.clear()
		if (typeof window === 'undefined') return
		try {
			localStorage.removeItem(this.storageKey)
		} catch {
			// Ignore errors
		}
	}
}

export const qrSvgPromiseCache = new LRUCache(QR_SVG_CACHE_MAX_SIZE)

type TQrPreviewSvgSource = {
	qrDesign: TQrDesign
	content: TQrContent
	uri: string
}

type TQrPreviewSvgCacheInput = TQrPreviewSvgSource & {
	id: string
	updatedAt: string
}

export const getQrCacheKey = ({
	id,
	updatedAt,
	uri,
}: {
	id: string
	updatedAt: string
	uri: string
}) => {
	return JSON.stringify(['qr-svg-preview', id, updatedAt, uri])
}

export const createQrPreviewSvg = async ({
	qrDesign,
	content,
	uri,
}: TQrPreviewSvgSource) => {
	const dataToEncode = getQrDataToEncode({
		qrUri: uri,
		qrContent: content,
	})
	const qrImageBuilder = await createQrImageBuilder({
		...qrDesign,
		data: dataToEncode,
	} as Parameters<typeof createQrImageBuilder>[0])

	const svg = await qrImageBuilder.getSvg({
		disablePaddingOnDefaultFrameIfNoBg: true,
	})

	return { svg, isLogoDropped: qrImageBuilder.isLogoDropped }
}

const optimizeQrPreviewSvg = async (originalSvg: string) => {
	const response = await fetch('/api/optimize-svg', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ svg: originalSvg }),
	})

	if (!response.ok) {
		return originalSvg
	}

	const json = (await response.json()) as { optimizedSvg?: unknown }
	if (typeof json.optimizedSvg !== 'string' || !json.optimizedSvg) {
		return originalSvg
	}

	return json.optimizedSvg
}

export const getCachedQrPreviewSvg = ({
	id,
	updatedAt,
	qrDesign,
	content,
	uri,
}: TQrPreviewSvgCacheInput) => {
	const cacheKey = getQrCacheKey({ id, updatedAt, uri })
	const cachedSvgPromise = qrSvgPromiseCache.get(cacheKey)
	if (cachedSvgPromise) {
		return cachedSvgPromise
	}

	const svgPromise = createQrPreviewSvg({ qrDesign, content, uri }).then(
		async ({ svg: originalSvg, isLogoDropped }) => {
			if (isLogoDropped) qrSvgPromiseCache.delete(cacheKey)
			try {
				return await optimizeQrPreviewSvg(originalSvg)
			} catch {
				return originalSvg
			}
		},
	)
	qrSvgPromiseCache.set(cacheKey, svgPromise)

	return svgPromise
}
